import {useId} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Text} from "@pg-design/text-module";
import {pluralize} from "@pg-mono/string-utils";

import {IPersonalizedRecommendationsTeaser} from "../../types/IPersonalizedHomepage";

interface IProps {
    teaser: IPersonalizedRecommendationsTeaser;
    onRecommendationsClick?: (ctaLabel: string) => void;
    /** When set, the CTA opens the Homematch tinder deck instead of navigating to the listing. */
    onOpenRecommendations?: () => void;
}

const offersPlural = pluralize(["ofertę", "oferty", "ofert"]);

const emptyStateIllustration = require("../../images/empty_picks_binoculars.svg");

/** Entry point to the Homematch swipe mode, presented as a stack of matched offers. */
export const PersonalizedHomepagePicksCard = (props: IProps) => {
    const {teaser, onRecommendationsClick, onOpenRecommendations} = props;
    const headingId = useId();
    const previews = teaser.imageUrls.slice(0, photoStackLayout.length);
    const lastPreviewIndex = previews.length - 1;
    // No count until the Homematch feed provides a real one — never invent it.
    const ctaLabel = teaser.offersCount ? `Zobacz ${teaser.offersCount} ${offersPlural(teaser.offersCount)}` : "Zobacz oferty";

    const handleRecommendationsActivate = () => {
        onRecommendationsClick?.(ctaLabel);
        onOpenRecommendations?.();
    };

    if (teaser.source !== "api") {
        return (
            <section className={emptyCardStyle} aria-labelledby={headingId}>
                <header className={emptyHeadStyle}>
                    <Text id={headingId} as="h2" variant="headline_6" className={headingStyle}>
                        Oferty są już w drodze...
                    </Text>
                    <Text as="p" variant="info_txt_2" className={subheadingStyle}>
                        Trwają poszukiwania rekomendacji dla Ciebie. Jak tylko się pojawią, damy Ci znać!
                    </Text>
                </header>

                <div className={emptyIllustrationAreaStyle}>
                    <img className={emptyIllustrationStyle} src={emptyStateIllustration} alt="" loading="lazy" />
                </div>
            </section>
        );
    }

    const photoStack = (
        <div className={photoStackStyle}>
            {previews.map((imageUrl, index) => (
                <div key={imageUrl} className={photoStackLayout[index]}>
                    <img className={photoStackImageStyle} src={imageUrl} alt="" loading="lazy" />
                    {index === lastPreviewIndex ? (
                        <Text as="span" variant="info_txt_3" className={photoStackBadgeStyle}>
                            {teaser.badgeLabel}
                        </Text>
                    ) : null}
                </div>
            ))}
        </div>
    );

    return (
        <section className={cardStyle} aria-labelledby={headingId}>
            <header className={headStyle}>
                <Text id={headingId} as="h2" variant="headline_6" className={headingStyle}>
                    Mieszkania wybrane dla Ciebie
                </Text>
                <Text as="p" variant="info_txt_2" className={subheadingStyle}>
                    Na bazie Twoich poprzednich wyszukiwań
                </Text>
            </header>

            {onOpenRecommendations ? (
                <button type="button" className={photoStackAreaStyle} aria-label={ctaLabel} onClick={handleRecommendationsActivate}>
                    {photoStack}
                </button>
            ) : (
                <a className={photoStackAreaStyle} href={teaser.href} aria-label={ctaLabel} onClick={handleRecommendationsActivate}>
                    {photoStack}
                </a>
            )}

            <Button
                variant="filled_secondary"
                size="medium"
                href={onOpenRecommendations ? undefined : teaser.href}
                className={buttonStyle}
                onClick={handleRecommendationsActivate}
            >
                {ctaLabel}
            </Button>
        </section>
    );
};

const cardStyle = css({
    display: "grid",
    gridTemplateAreas: `"head" "photo" "button"`,
    gridTemplateColumns: "minmax(0, 1fr)",
    justifyItems: "center",
    alignContent: "start",
    rowGap: 1.5,
    boxSizing: "border-box",
    height: "100%",
    padding: 2,
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    overflow: "hidden",

    md: {
        minHeight: "41.1rem"
    },

    sm: {
        gridTemplateAreas: `"head photo" "button photo"`,
        gridTemplateColumns: "auto auto",
        justifyContent: "center",
        justifyItems: "start",
        alignContent: "center",
        columnGap: 8,
        rowGap: 5,
        padding: 3
    },

    lg: {
        gridTemplateAreas: `"head" "photo" "button"`,
        gridTemplateColumns: "minmax(0, 1fr)",
        justifyContent: "stretch",
        justifyItems: "center",
        alignContent: "start",
        rowGap: 2.5
    }
});

const headStyle = css({
    gridArea: "head",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.2rem",
    width: "100%",
    textAlign: "center",

    sm: {
        alignItems: "flex-start",
        alignSelf: "end",
        textAlign: "left"
    },

    lg: {
        alignItems: "center",
        alignSelf: "auto",
        textAlign: "center"
    }
});

const headingStyle = css({
    margin: 0,
    color: "var(--colors-secondary)"
});

const subheadingStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)"
});

/**
 * Mobile keeps the tall vertical placeholder, sm–md renders the compact
 * Figma bar (small illustration + texts), lg returns to the vertical card
 * sharing the bento min-height.
 */
const emptyCardStyle = css({
    display: "grid",
    gridTemplateAreas: `"head" "photo"`,
    gridTemplateColumns: "minmax(0, 1fr)",
    gridTemplateRows: "auto minmax(0, 1fr)",
    justifyItems: "center",
    alignContent: "stretch",
    rowGap: 1.5,
    boxSizing: "border-box",
    height: "100%",
    padding: 2,
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    overflow: "hidden",

    sm: {
        gridTemplateAreas: `"photo head"`,
        gridTemplateColumns: "auto minmax(0, 1fr)",
        gridTemplateRows: "auto",
        alignItems: "center",
        alignContent: "center",
        justifyItems: "start",
        columnGap: 2.5,
        padding: 3
    },

    lg: {
        gridTemplateAreas: `"head" "photo"`,
        gridTemplateColumns: "minmax(0, 1fr)",
        gridTemplateRows: "auto minmax(0, 1fr)",
        alignItems: "stretch",
        justifyItems: "center",
        alignContent: "stretch",
        rowGap: 2.5,
        minHeight: "41.1rem"
    }
});

const emptyHeadStyle = css({
    gridArea: "head",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.2rem",
    width: "100%",
    textAlign: "center",

    sm: {
        alignItems: "flex-start",
        alignSelf: "center",
        textAlign: "left"
    },

    lg: {
        alignItems: "center",
        alignSelf: "auto",
        textAlign: "center"
    }
});

const emptyIllustrationAreaStyle = css({
    gridArea: "photo",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    sm: {
        alignSelf: "center"
    },

    lg: {
        alignSelf: "auto"
    }
});

const emptyIllustrationStyle = css({
    width: "17.3rem",
    height: "25rem",

    sm: {
        width: "6.1rem",
        height: "8.8rem"
    },

    lg: {
        width: "17.3rem",
        height: "25rem"
    }
});

const photoStackAreaStyle = css({
    gridArea: "photo",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",

    "& > div": {
        transition: "transform 0.2s ease"
    },

    _hover: {
        "& > div": {
            transform: "scale(1.03)"
        }
    },

    sm: {
        alignSelf: "center",
        pr: 5
    },

    lg: {
        alignSelf: "auto",
        pr: 0
    }
});

const photoStackStyle = css({
    position: "relative",
    width: "21rem",
    height: "25rem"
});

const photoCardStyle = {
    position: "absolute",
    width: "16.4rem",
    height: "20.4rem",
    borderRadius: 2,
    overflow: "hidden"
} as const;

const photoStackBackStyle = css({
    ...photoCardStyle,
    top: "2.4rem",
    left: 0,
    boxShadow: "0 10px 22px 0 rgba(20, 23, 28, 0.35)",
    transform: "rotate(9deg)"
});

const photoStackMiddleStyle = css({
    ...photoCardStyle,
    top: 0,
    left: "2.4rem",
    boxShadow: "0 12px 26px 0 rgba(20, 23, 28, 0.4)",
    transform: "rotate(-7deg)"
});

const photoStackFrontStyle = css({
    ...photoCardStyle,
    top: "1.6rem",
    left: "1.8rem",
    boxShadow: "0 16px 34px 0 rgba(20, 23, 28, 0.5)",
    transform: "rotate(1.5deg)"
});

const photoStackLayout = [photoStackBackStyle, photoStackMiddleStyle, photoStackFrontStyle];

const photoStackImageStyle = css({
    width: "100%",
    height: "100%",
    objectFit: "cover"
});

const photoStackBadgeStyle = css({
    position: "absolute",
    top: "1.2rem",
    left: "1.2rem",
    maxWidth: "13.2rem",
    px: 1.5,
    py: 1,
    overflow: "hidden",
    color: "var(--colors-white)",
    backgroundColor: "rgba(35, 35, 45, 0.82)",
    borderRadius: "999px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const buttonStyle = css({
    gridArea: "button",
    minWidth: "20rem",

    sm: {
        alignSelf: "start"
    },

    lg: {
        alignSelf: "auto"
    }
});
import {useId} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Text} from "@pg-design/text-module";
import {pluralize} from "@pg-mono/string-utils";

import {IPersonalizedRecommendationsTeaser} from "../../types/IPersonalizedHomepage";

interface IProps {
    teaser: IPersonalizedRecommendationsTeaser;
    onRecommendationsClick?: (ctaLabel: string) => void;
    /** When set, the CTA opens the Homematch tinder deck instead of navigating to the listing. */
    onOpenRecommendations?: () => void;
}

const offersPlural = pluralize(["ofertę", "oferty", "ofert"]);

const emptyStateIllustration = require("../../images/empty_picks_binoculars.svg");

/** Entry point to the Homematch swipe mode, presented as a stack of matched offers. */
export const PersonalizedHomepagePicksCard = (props: IProps) => {
    const {teaser, onRecommendationsClick, onOpenRecommendations} = props;
    const headingId = useId();
    const previews = teaser.imageUrls.slice(0, photoStackLayout.length);
    const lastPreviewIndex = previews.length - 1;
    // No count until the Homematch feed provides a real one — never invent it.
    const ctaLabel = teaser.offersCount ? `Zobacz ${teaser.offersCount} ${offersPlural(teaser.offersCount)}` : "Zobacz oferty";

    const handleRecommendationsActivate = () => {
        onRecommendationsClick?.(ctaLabel);
        onOpenRecommendations?.();
    };

    if (teaser.source !== "api") {
        return (
            <section className={emptyCardStyle} aria-labelledby={headingId}>
                <header className={emptyHeadStyle}>
                    <Text id={headingId} as="h2" variant="headline_6" className={headingStyle}>
                        Oferty są już w drodze...
                    </Text>
                    <Text as="p" variant="info_txt_2" className={subheadingStyle}>
                        Trwają poszukiwania rekomendacji dla Ciebie. Jak tylko się pojawią, damy Ci znać!
                    </Text>
                </header>

                <div className={emptyIllustrationAreaStyle}>
                    <img className={emptyIllustrationStyle} src={emptyStateIllustration} alt="" loading="lazy" />
                </div>
            </section>
        );
    }

    const photoStack = (
        <div className={photoStackStyle}>
            {previews.map((imageUrl, index) => (
                <div key={imageUrl} className={photoStackLayout[index]}>
                    <img className={photoStackImageStyle} src={imageUrl} alt="" loading="lazy" />
                    {index === lastPreviewIndex ? (
                        <Text as="span" variant="info_txt_3" className={photoStackBadgeStyle}>
                            {teaser.badgeLabel}
                        </Text>
                    ) : null}
                </div>
            ))}
        </div>
    );

    return (
        <section className={cardStyle} aria-labelledby={headingId}>
            <header className={headStyle}>
                <Text id={headingId} as="h2" variant="headline_6" className={headingStyle}>
                    Mieszkania wybrane dla Ciebie
                </Text>
                <Text as="p" variant="info_txt_2" className={subheadingStyle}>
                    Na bazie Twoich poprzednich wyszukiwań
                </Text>
            </header>

            {onOpenRecommendations ? (
                <button type="button" className={photoStackAreaStyle} aria-label={ctaLabel} onClick={handleRecommendationsActivate}>
                    {photoStack}
                </button>
            ) : (
                <a className={photoStackAreaStyle} href={teaser.href} aria-label={ctaLabel} onClick={handleRecommendationsActivate}>
                    {photoStack}
                </a>
            )}

            <Button
                variant="filled_secondary"
                size="medium"
                href={onOpenRecommendations ? undefined : teaser.href}
                className={buttonStyle}
                onClick={handleRecommendationsActivate}
            >
                {ctaLabel}
            </Button>
        </section>
    );
};

const cardStyle = css({
    display: "grid",
    gridTemplateAreas: `"head" "photo" "button"`,
    gridTemplateColumns: "minmax(0, 1fr)",
    justifyItems: "center",
    alignContent: "start",
    rowGap: 1.5,
    boxSizing: "border-box",
    height: "100%",
    padding: 2,
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    overflow: "hidden",

    md: {
        minHeight: "41.1rem"
    },

    sm: {
        gridTemplateAreas: `"head photo" "button photo"`,
        gridTemplateColumns: "auto auto",
        justifyContent: "center",
        justifyItems: "start",
        alignContent: "center",
        columnGap: 8,
        rowGap: 5,
        padding: 3
    },

    lg: {
        gridTemplateAreas: `"head" "photo" "button"`,
        gridTemplateColumns: "minmax(0, 1fr)",
        justifyContent: "stretch",
        justifyItems: "center",
        alignContent: "start",
        rowGap: 2.5
    }
});

const headStyle = css({
    gridArea: "head",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.2rem",
    width: "100%",
    textAlign: "center",

    sm: {
        alignItems: "flex-start",
        alignSelf: "end",
        textAlign: "left"
    },

    lg: {
        alignItems: "center",
        alignSelf: "auto",
        textAlign: "center"
    }
});

const headingStyle = css({
    margin: 0,
    color: "var(--colors-secondary)"
});

const subheadingStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)"
});

/**
 * Mobile keeps the tall vertical placeholder, sm–md renders the compact
 * Figma bar (small illustration + texts), lg returns to the vertical card
 * sharing the bento min-height.
 */
const emptyCardStyle = css({
    display: "grid",
    gridTemplateAreas: `"head" "photo"`,
    gridTemplateColumns: "minmax(0, 1fr)",
    gridTemplateRows: "auto minmax(0, 1fr)",
    justifyItems: "center",
    alignContent: "stretch",
    rowGap: 1.5,
    boxSizing: "border-box",
    height: "100%",
    padding: 2,
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,
    overflow: "hidden",

    sm: {
        gridTemplateAreas: `"photo head"`,
        gridTemplateColumns: "auto minmax(0, 1fr)",
        gridTemplateRows: "auto",
        alignItems: "center",
        alignContent: "center",
        justifyItems: "start",
        columnGap: 2.5,
        padding: 3
    },

    lg: {
        gridTemplateAreas: `"head" "photo"`,
        gridTemplateColumns: "minmax(0, 1fr)",
        gridTemplateRows: "auto minmax(0, 1fr)",
        alignItems: "stretch",
        justifyItems: "center",
        alignContent: "stretch",
        rowGap: 2.5,
        minHeight: "41.1rem"
    }
});

const emptyHeadStyle = css({
    gridArea: "head",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.2rem",
    width: "100%",
    textAlign: "center",

    sm: {
        alignItems: "flex-start",
        alignSelf: "center",
        textAlign: "left"
    },

    lg: {
        alignItems: "center",
        alignSelf: "auto",
        textAlign: "center"
    }
});

const emptyIllustrationAreaStyle = css({
    gridArea: "photo",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    sm: {
        alignSelf: "center"
    },

    lg: {
        alignSelf: "auto"
    }
});

const emptyIllustrationStyle = css({
    width: "17.3rem",
    height: "25rem",

    sm: {
        width: "6.1rem",
        height: "8.8rem"
    },

    lg: {
        width: "17.3rem",
        height: "25rem"
    }
});

const photoStackAreaStyle = css({
    gridArea: "photo",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",

    "& > div": {
        transition: "transform 0.2s ease"
    },

    _hover: {
        "& > div": {
            transform: "scale(1.03)"
        }
    },

    sm: {
        alignSelf: "center",
        pr: 5
    },

    lg: {
        alignSelf: "auto",
        pr: 0
    }
});

const photoStackStyle = css({
    position: "relative",
    width: "21rem",
    height: "25rem"
});

const photoCardStyle = {
    position: "absolute",
    width: "16.4rem",
    height: "20.4rem",
    borderRadius: 2,
    overflow: "hidden"
} as const;

const photoStackBackStyle = css({
    ...photoCardStyle,
    top: "2.4rem",
    left: 0,
    boxShadow: "0 10px 22px 0 rgba(20, 23, 28, 0.35)",
    transform: "rotate(9deg)"
});

const photoStackMiddleStyle = css({
    ...photoCardStyle,
    top: 0,
    left: "2.4rem",
    boxShadow: "0 12px 26px 0 rgba(20, 23, 28, 0.4)",
    transform: "rotate(-7deg)"
});

const photoStackFrontStyle = css({
    ...photoCardStyle,
    top: "1.6rem",
    left: "1.8rem",
    boxShadow: "0 16px 34px 0 rgba(20, 23, 28, 0.5)",
    transform: "rotate(1.5deg)"
});

const photoStackLayout = [photoStackBackStyle, photoStackMiddleStyle, photoStackFrontStyle];

const photoStackImageStyle = css({
    width: "100%",
    height: "100%",
    objectFit: "cover"
});

const photoStackBadgeStyle = css({
    position: "absolute",
    top: "1.2rem",
    left: "1.2rem",
    maxWidth: "13.2rem",
    px: 1.5,
    py: 1,
    overflow: "hidden",
    color: "var(--colors-white)",
    backgroundColor: "rgba(35, 35, 45, 0.82)",
    borderRadius: "999px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const buttonStyle = css({
    gridArea: "button",
    minWidth: "20rem",

    sm: {
        alignSelf: "start"
    },

    lg: {
        alignSelf: "auto"
    }
});
